import React, { useCallback, useEffect } from 'react';
import { HelpMessage, ListView, ListViewActions, Page } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useActions, useDocumentTitle } from '~/hooks';
import { ErrorPage, PageLoader } from '~/routes/public/pages';
import AvailabilityPeriodRow from './AvailabilityPeriodRow';

const initialState = { isReady: false, data: null };
const handlers = {
  ready: ({ data }) => ({ isReady: true, dialog: null, data, editIndex: null }),
  edit: (editIndex) => ({ editIndex }),
};

function AvailabilityPeriods({ member }) {
  useDocumentTitle(`${member.name} Capacity`);

  const api = useApi();
  const { workspace } = useWorkspace();
  const [{ isReady, data, editIndex }, actions] = useActions(handlers, initialState);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www.workspaces(workspace.id).members(member.id).availabilityPeriods().get();

      actions.ready({ data });
    } catch (error) {
      actions.ready({ data: null });
    }
  }, [actions, workspace.id, member.id, api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!isReady && !data) return <PageLoader />;
  if (!data) return <ErrorPage publicSite={false} />;
  return (
    <>
      <HelpMessage spaceTop>
        In order to allocate team members to project work and accurately calculate billable utilization, Ruddr needs to
        know which days each team member is available to work and the number of hours available each day.
      </HelpMessage>

      <Page.ListView>
        <ListView>
          <ListView.Header>
            <ListView.Column minWidth="16rem">Date Range</ListView.Column>
            <ListView.Column minWidth="25rem" align="center">
              Capacity
            </ListView.Column>
            <ListView.Column align="right" width="9rem">
              Weekly Hours
            </ListView.Column>
            <ListView.Column align="right" width="9rem">
              Days in Period
            </ListView.Column>
            <ListViewActions.Column />
          </ListView.Header>

          <ListView.Body>
            {data.map((item, index) => (
              <AvailabilityPeriodRow
                key={item.id}
                period={item}
                periods={data}
                member={member}
                disableActions={editIndex !== null}
                isEditing={editIndex === index}
                onEdit={() => actions.edit(index)}
                onCancel={() => actions.edit(null)}
                onSaved={fetchData}
                onDeleted={fetchData}
              />
            ))}
            <AvailabilityPeriodRow
              period={{}}
              periods={data}
              member={member}
              isEditing={editIndex === -1}
              disableActions={editIndex !== null}
              onEdit={() => actions.edit(-1)}
              onCancel={() => actions.edit(null)}
              onSaved={fetchData}
            />
          </ListView.Body>

          <ListView.Status total={data.length} label="Capacity Period" />
        </ListView>
      </Page.ListView>
    </>
  );
}

export default AvailabilityPeriods;
